/**
 * Etiket yazdırma dialogu: ürün seç → şablon seç → önizle → bas.
 *
 * Yazdırma `react-to-print` ile yapılır (projedeki yerleşik kalıp), sayfa
 * boyutu şablona göre dinamik üretilir. Yazdırılacak içerik ekran dışında
 * tutulur; görsellerin (barkod/QR) yüklenmesi için DOM'da bulunması gerekiyor,
 * bu yüzden `display: none` yerine ekran dışına konumlandırıldı.
 */

import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BoltIcon from '@mui/icons-material/Bolt';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../../../contexts/AuthContext';
import { LabelTemplate } from '../../../types/labelTemplate.types';
import { expandInstances, imposeLabels, labelsPerPage, validateLayout } from '../utils/labelImposition';
import { buildLabelPageStyle, getPrintSheetSize } from '../utils/printViaBrowser';
import { exportLabelPagesToPdf } from '../utils/labelPdfExport';
import { nextFrame, waitForImages } from '../utils/hiddenRenderHost';
import { perfMeasure } from '../utils/labelPerf';
import { printLabelsRaw, resolveLanguage } from '../utils/printViaRaw';
import { LabelPrinterSelection, RAW_SUPPORTED_LANGUAGES } from '../../../services/labelPrintService';
import { inventoryItemToPlaceholders } from '../dataSources/inventoryLabelSource';
import LabelSheetPreview from '../render/LabelSheetPreview';
import InventoryPicker, { PickedRow } from './InventoryPicker';
import LabelPrinterSelect from './LabelPrinterSelect';

export interface LabelPrintDialogProps {
  open: boolean;
  onClose: () => void;
  templates: LabelTemplate[];
  initialTemplateId?: string;
}

const LabelPrintDialog: React.FC<LabelPrintDialogProps> = ({ open, onClose, templates, initialTemplateId }) => {
  const { categories, brands, companyName } = useAuth();

  const [rows, setRows] = useState<PickedRow[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [startOffset, setStartOffset] = useState(0);
  const [previewPage, setPreviewPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printer, setPrinter] = useState<LabelPrinterSelection | null>(null);
  const [rawPrinting, setRawPrinting] = useState(false);
  const [rawProgress, setRawProgress] = useState<{ done: number; total: number } | null>(null);
  const [pdfProgress, setPdfProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Yazdırma sayfaları yalnızca gerektiğinde DOM'a alınır. Sürekli çizili
   * tutmak 100 etikette ürün eklemeyi kilitliyordu: her seçim tüm sayfaları
   * yeniden render ediyor, her etiket de bir barkod üretimi ve metin sığdırma
   * ölçümü demek.
   */
  const [sheetMounted, setSheetMounted] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const fallback = templates.find((t) => t.isDefault) ?? templates[0];
    setTemplateId(initialTemplateId ?? fallback?.id ?? '');
    setPreviewPage(0);
    setError(null);
    setInfo(null);
  }, [open, initialTemplateId, templates]);

  const template = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templates, templateId],
  );

  // Kategori/marka adları AuthContext cache'inden gelir; ek istek gerekmiyor
  const nameMaps = useMemo(() => {
    const categoryNames: Record<string, string> = {};
    const brandNames: Record<string, string> = {};
    (categories ?? []).forEach((c: { id?: string; name?: string }) => {
      if (c?.id) categoryNames[c.id] = c.name ?? '';
    });
    (brands ?? []).forEach((b: { id?: string; name?: string }) => {
      if (b?.id) brandNames[b.id] = b.name ?? '';
    });
    return { categoryNames, brandNames, companyName: companyName ?? '' };
  }, [categories, brands, companyName]);

  const instances = useMemo(
    () =>
      perfMeasure(`yer tutucu çözümleme (${rows.length} ürün)`, () =>
        expandInstances(
          rows.map((row) => ({
            id: row.item.id,
            data: inventoryItemToPlaceholders(row.item, nameMaps),
            quantity: row.quantity,
          })),
        ),
      ),
    [rows, nameMaps],
  );

  const pages = useMemo(
    () =>
      perfMeasure(`sayfa dizilimi (${instances.length} etiket)`, () =>
        template ? imposeLabels(instances, template, { startOffset }) : [],
      ),
    [instances, template, startOffset],
  );

  /**
   * Önizleme sayfası bilinçli olarak geride bırakılıyor: barkod üretimi ve
   * metin sığdırma ölçümü içerdiği için her ürün eklemesinde yeniden çizmek
   * arayüzü takılıyor gösteriyor. Ekleme akıcı kalır, önizleme boşluk bulunca
   * güncellenir.
   */
  const deferredPages = useDeferredValue(pages);

  const warnings = useMemo(() => (template ? validateLayout(template) : []), [template]);
  const missingBarcode = useMemo(
    () => rows.filter((r) => !r.item.barcode).length,
    [rows],
  );

  useEffect(() => {
    if (previewPage > Math.max(0, pages.length - 1)) setPreviewPage(0);
  }, [pages.length, previewPage]);

  const pageStyle = useMemo(() => (template ? buildLabelPageStyle(template) : ''), [template]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: template ? `Etiket_${template.name}` : 'Etiket',
    pageStyle,
    // Sayfalar normalde DOM'da değil; yazdırma diyaloğundan hemen önce çizilir
    onBeforeGetContent: async () => {
      setPreparingPrint(true);
      setSheetMounted(true);
      await nextFrame();
      if (printRef.current) await waitForImages(printRef.current);
      setPreparingPrint(false);
    },
    onAfterPrint: () => setSheetMounted(false),
    onPrintError: (_location, err) => {
      setPreparingPrint(false);
      setSheetMounted(false);
      setError(err?.message ?? 'Yazdırma başarısız');
    },
  });

  const handlePdf = useCallback(async () => {
    if (!template) return;
    setExporting(true);
    setError(null);
    setInfo(null);
    setPdfProgress({ done: 0, total: pages.length });
    try {
      // Sayfaları PDF üreticisi doğrudan canvas'a çizer; DOM'a gerek yok
      await exportLabelPagesToPdf(template, pages, getPrintSheetSize(template), {
        fileName: `etiket-${template.name.replace(/\s+/g, '-').toLowerCase()}.pdf`,
        onProgress: (done, total) => setPdfProgress({ done, total }),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF oluşturulamadı');
    } finally {
      setExporting(false);
      setPdfProgress(null);
    }
  }, [template, pages]);

  /**
   * Sessiz yazdırma sayfa dizilimini kullanmaz: etiket yazıcısı ruloyu tek tek
   * besler, kopyalar yazıcının kendi sayacına bırakılır.
   */
  const handleRawPrint = useCallback(async () => {
    if (!template || !printer) return;
    setRawPrinting(true);
    setError(null);
    setInfo(null);
    setRawProgress({ done: 0, total: rows.length });
    try {
      const result = await printLabelsRaw(
        template,
        rows.map((row) => ({
          data: inventoryItemToPlaceholders(row.item, nameMaps),
          copies: row.quantity,
        })),
        {
          printer,
          onProgress: (done, total) => setRawProgress({ done, total }),
        },
      );
      setInfo(`${result.printed} etiket yazıcıya gönderildi.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Etiket yazdırılamadı');
    } finally {
      setRawPrinting(false);
      setRawProgress(null);
    }
  }, [template, printer, rows, nameMaps]);

  const totalLabels = instances.length;
  const perPage = template ? labelsPerPage(template) : 1;
  const rawLanguage = template ? resolveLanguage(template, { printer }) : null;
  const rawSupported = !!rawLanguage && RAW_SUPPORTED_LANGUAGES.includes(rawLanguage);

  // Önizleme ölçeği: sayfa panele sığacak şekilde
  const previewScale = useMemo(() => {
    if (!template) return 1;
    const sheet = getPrintSheetSize(template);
    const availablePx = 420;
    const sheetPx = (sheet.widthMm * 96) / 25.4;
    return Math.min(1, availablePx / sheetPx);
  }, [template]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <PrintIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          Etiket Yazdır
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ display: 'flex', gap: 2, overflow: 'hidden', p: 2 }}>
        {/* Sol: ürün seçimi */}
        <Box sx={{ flex: 1.4, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <InventoryPicker rows={rows} onChange={setRows} />
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Sağ: şablon ve önizleme */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 360, overflow: 'hidden' }}>
          <Stack spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              select
              size="small"
              label="Etiket Şablonu"
              fullWidth
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.length === 0 && (
                <MenuItem value="" disabled>
                  Henüz şablon yok — önce bir şablon oluşturun
                </MenuItem>
              )}
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id ?? ''}>
                  {t.name} · {t.labelWidthMm}×{t.labelLengthMm} mm
                  {t.isDefault ? ' (varsayılan)' : ''}
                </MenuItem>
              ))}
            </TextField>

            {template?.layout.mode === 'sheet' && (
              <TextField
                type="number"
                size="small"
                label="Baştan atlanacak göz"
                fullWidth
                value={startOffset}
                onChange={(e) => setStartOffset(Math.max(0, parseInt(e.target.value, 10) || 0))}
                inputProps={{ min: 0, max: perPage - 1, step: 1 }}
                helperText={`Yarım kullanılmış sayfa için. Sayfada ${perPage} göz var.`}
              />
            )}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label={`${totalLabels} etiket`} />
              <Chip size="small" variant="outlined" label={`${pages.length} sayfa`} />
              {template && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={template.layout.mode === 'roll' ? 'Rulo' : `Sayfada ${perPage} adet`}
                />
              )}
            </Stack>

            {missingBarcode > 0 && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {missingBarcode} ürünün barkodu yok; o etiketlerde barkod alanı boş çıkacak.
              </Alert>
            )}
            {warnings.map((w) => (
              <Alert key={w} severity="warning" sx={{ py: 0 }}>
                {w}
              </Alert>
            ))}
            {error && <Alert severity="error">{error}</Alert>}
            {info && <Alert severity="success" sx={{ py: 0 }}>{info}</Alert>}

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Doğrudan yazıcıya bas
              </Typography>
            </Divider>

            <LabelPrinterSelect value={printer} onChange={setPrinter} />

            {template && !rawSupported && (
              <Alert severity="info" sx={{ py: 0 }}>
                {rawLanguage} dili için doğrudan yazdırma henüz yok. Desteklenen diller:{' '}
                {RAW_SUPPORTED_LANGUAGES.join(', ')}. Yazıcı seçiminden veya şablon ayarlarından
                dili değiştirebilirsiniz.
              </Alert>
            )}
          </Stack>

          <Divider />

          {/* Önizleme */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Önizleme
            </Typography>
            <IconButton size="small" disabled={previewPage <= 0} onClick={() => setPreviewPage((p) => p - 1)}>
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="caption">
              {pages.length === 0 ? '0 / 0' : `${previewPage + 1} / ${pages.length}`}
            </Typography>
            <IconButton
              size="small"
              disabled={previewPage >= pages.length - 1}
              onClick={() => setPreviewPage((p) => p + 1)}
            >
              <NavigateNextIcon />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'grey.200', p: 2, borderRadius: 1 }}>
            {template && deferredPages.length > 0 ? (
              <LabelSheetPreview
                template={template}
                pages={deferredPages}
                onlyPageIndex={deferredPages[previewPage]?.index ?? 0}
                scale={previewScale}
                showBounds
              />
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {templates.length === 0
                    ? 'Önce bir etiket şablonu oluşturun.'
                    : 'Soldan ürün seçtiğinizde önizleme burada görünecek.'}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {rawProgress
            ? `Yazdırılıyor… ${rawProgress.done} / ${rawProgress.total}`
            : pdfProgress
              ? `PDF hazırlanıyor… ${pdfProgress.done} / ${pdfProgress.total} sayfa`
              : preparingPrint
                ? `${pages.length} sayfa hazırlanıyor…`
                : 'İpucu: Yazdırma penceresinde hedef olarak "PDF olarak kaydet" seçerseniz metin vektör kalır, en keskin sonucu verir.'}
        </Typography>
        <Button onClick={onClose}>Kapat</Button>
        <Tooltip
          title={
            printer
              ? `${printer.label} üzerine yazdırma penceresi açmadan bas`
              : 'Önce bir etiket yazıcısı seçin'
          }
        >
          <span>
            <Button
              onClick={handleRawPrint}
              startIcon={rawPrinting ? <CircularProgress size={16} /> : <BoltIcon />}
              disabled={!template || !printer || !rawSupported || rows.length === 0 || rawPrinting}
            >
              Yazıcıya Bas
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Sayfaları PDF dosyası olarak indir">
          <span>
            <Button
              onClick={handlePdf}
              startIcon={exporting ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              disabled={!template || pages.length === 0 || exporting}
            >
              PDF İndir
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={preparingPrint ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={!template || pages.length === 0 || preparingPrint || exporting}
        >
          Yazdır ({totalLabels})
        </Button>
      </DialogActions>

      {/* Yazdırılacak içerik — ekran dışında; yalnızca yazdırma anında çizilir */}
      <Box sx={{ position: 'fixed', left: -10000, top: 0, width: 0, height: 0, overflow: 'hidden' }}>
        <div ref={printRef}>
          {template && sheetMounted && <LabelSheetPreview template={template} pages={pages} forPrint />}
        </div>
      </Box>
    </Dialog>
  );
};

export default LabelPrintDialog;
